import React, { useState, useEffect, useMemo } from 'react';
import { Users, Shield, Ban, CheckCircle, Search, Filter, ChevronLeft, ChevronRight, Crown, RefreshCw, Gift } from 'lucide-react';
import { db, useAuth } from '../../services/firebase';
import { collection, onSnapshot, doc, updateDoc, addDoc } from 'firebase/firestore';

interface UserData {
    id: string;
    name: string;
    email: string;
    role: 'user' | 'subscriber' | 'admin';
    status: 'active' | 'banned';
    joinDate: string;
    lastLogin?: any;
    createdAt?: any;
    subscriptionExpiresAt?: Date;
    dailyLupas?: number;
    extraLupas?: number;
    adLupas?: number;
    archiveKeys?: number;
}

const UserManager: React.FC = () => {
    const { user: authUser } = useAuth();
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);

    // Filters & Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;

    useEffect(() => {
        if (!db) return;

        const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
            const usersList = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    name: data.name || 'Desconhecido',
                    email: data.email || 'N/A',
                    role: data.role || 'user',
                    status: data.status || 'active',
                    joinDate: data.createdAt ? new Date(data.createdAt.seconds * 1000).toLocaleDateString() : 'N/A',
                    subscriptionExpiresAt: data.subscriptionExpiresAt ? new Date(data.subscriptionExpiresAt.seconds * 1000) : undefined,
                    dailyLupas: data.dailyLupas !== undefined ? data.dailyLupas : 15,
                    extraLupas: data.extraLupas || 0,
                    adLupas: data.adLupas || 0,
                    archiveKeys: data.archiveKeys || 0
                } as UserData;
            });
            // Sort by creation date descending (newest first) as default if possible, otherwise keep order
            usersList.sort((a, b) => b.joinDate.localeCompare(a.joinDate));
            setUsers(usersList);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Derived State
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = roleFilter === 'all' || u.role === roleFilter;
            return matchesSearch && matchesRole;
        });
    }, [users, searchTerm, roleFilter]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage) || 1;
    const paginatedUsers = filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

    // Reset page if filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, roleFilter]);

    // Bonus Modal State
    const [bonusModal, setBonusModal] = useState({
        isOpen: false,
        userId: '',
        userName: '',
        rewardType: 'lupas' as 'lupas' | 'vip_days' | 'keys',
        amount: 10,
        reason: ''
    });

    const openBonusModal = (userId: string, userName: string) => {
        setBonusModal({
            isOpen: true,
            userId,
            userName,
            rewardType: 'lupas',
            amount: 10,
            reason: ''
        });
    };

    const closeBonusModal = () => {
        setBonusModal({ ...bonusModal, isOpen: false });
    };

    const handleApplyBonus = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!db || !authUser) return;

        const targetUser = users.find(u => u.id === bonusModal.userId);
        if (!targetUser) return;

        try {
            const userRef = doc(db, 'users', bonusModal.userId);
            let updates: any = {};

            if (bonusModal.rewardType === 'lupas') {
                updates.extraLupas = (targetUser.extraLupas || 0) + bonusModal.amount;
            } else if (bonusModal.rewardType === 'keys') {
                updates.archiveKeys = (targetUser.archiveKeys || 0) + bonusModal.amount;
            } else if (bonusModal.rewardType === 'vip_days') {
                updates.role = 'subscriber';
                let baseDate = new Date();
                if (targetUser.role === 'subscriber' && targetUser.subscriptionExpiresAt && targetUser.subscriptionExpiresAt > new Date()) {
                    baseDate = new Date(targetUser.subscriptionExpiresAt);
                }
                baseDate.setDate(baseDate.getDate() + bonusModal.amount);
                updates.subscriptionExpiresAt = baseDate;
            }

            // Execute Updates
            await updateDoc(userRef, updates);

            // Create Log
            await addDoc(collection(db, 'admin_logs'), {
                action: 'grant_bonus',
                adminId: authUser.uid,
                targetUserId: bonusModal.userId,
                targetUserName: bonusModal.userName,
                rewardType: bonusModal.rewardType,
                amount: bonusModal.amount,
                reason: bonusModal.reason,
                timestamp: new Date()
            });

            alert(`Bonus aplicado com sucesso para ${bonusModal.userName}!`);
            closeBonusModal();
        } catch (error) {
            console.error("Erro ao aplicar bônus:", error);
            alert("Erro ao aplicar bônus.");
        }
    };

    const changeRole = async (id: string, newRole: string) => {
        if (!db) return;

        const updates: any = { role: newRole };
        if (newRole !== 'subscriber') {
            updates.subscriptionExpiresAt = null;
        }

        try {
            await updateDoc(doc(db, 'users', id), updates);
        } catch (error) {
            console.error("Erro ao atualizar role:", error);
            alert("Erro ao atualizar permissão.");
        }
    };

    const toggleBan = async (id: string, currentStatus: string) => {
        if (!db) return;
        const newStatus = currentStatus === 'active' ? 'banned' : 'active';
        try {
            await updateDoc(doc(db, 'users', id), {
                status: newStatus
            });
        } catch (error) {
            console.error("Erro ao banir usuário:", error);
        }
    };

    const handleResetAccount = async (id: string) => {
        if (!confirm("Tem certeza? Isso apagará TODO o progresso e casos ativos do Detetive (o usuário será resetado do zero ao logar novamente).")) return;
        try {
            await updateDoc(doc(db, 'users', id), {
                gameState: null
            });
            alert("Progresso do usuário apagado com sucesso!");
        } catch (error) {
            console.error("Erro ao resetar usuário:", error);
            alert("Falha ao resetar progresso.");
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Carregando agentes...</div>;

    return (
        <div className="animate-in fade-in duration-500 flex flex-col h-full max-h-full">
            <div className="flex justify-between items-center mb-6 shrink-0">
                <h1 className="text-3xl font-bold text-gray-800">Gerenciamento de Agentes</h1>
                <div className="text-sm text-gray-500">Total: <span className="font-bold text-gray-900">{users.length}</span> usuários</div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col sm:flex-row gap-4 shrink-0">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por nome, email ou ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter size={18} className="text-gray-400" />
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-amber-500 min-w-[150px] outline-none bg-white"
                    >
                        <option value="all">Todas as Roles</option>
                        <option value="user">User (Básico)</option>
                        <option value="subscriber">Subscriber (VIP)</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="overflow-auto flex-1">
                    <table className="w-full text-left relative">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                            <tr>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Agente</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Status</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Nível & Energia</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right whitespace-nowrap">Recursos (VIP/Lupa)</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right whitespace-nowrap">Sistema</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {paginatedUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-gray-500">Nenhum agente encontrado com estes filtros.</td>
                                </tr>
                            ) : paginatedUsers.map(u => (
                                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 w-[25%]">
                                        <div className="font-bold text-gray-800 truncate max-w-[200px]" title={u.name}>{u.name}</div>
                                        <div className="text-xs text-gray-500 truncate max-w-[200px]" title={u.email}>{u.email}</div>
                                        <div className="text-[10px] text-gray-400 font-mono mt-1 pt-1 border-t border-gray-100 inline-block">{u.id}</div>
                                    </td>
                                    <td className="p-4 w-[15%]">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="p-4 w-[20%]">
                                        <div className={`flex items-center gap-1 text-sm ${u.role === 'admin' ? 'text-purple-600 font-bold' : u.role === 'subscriber' ? 'text-amber-600 font-bold' : 'text-gray-600'}`}>
                                            {u.role === 'admin' && <Shield size={14} />}
                                            {u.role === 'subscriber' && <Crown size={14} />}
                                            <span className="uppercase">{u.role}</span>
                                        </div>
                                        <div className="text-[10px] text-gray-500 mt-1 font-bold">
                                            Lupas: <span className="text-amber-500" title="Diárias">{(u.dailyLupas || 0)}</span> | <span className="text-blue-500" title="Anúncios">{(u.adLupas || 0)}</span> | <span className="text-green-500" title="Extras/Compradas">{(u.extraLupas || 0)}</span> 🔍
                                            <span className="ml-2 text-yellow-600 block sm:inline">Chaves: {(u.archiveKeys || 0)} 🔑</span>
                                        </div>
                                        {u.subscriptionExpiresAt && u.role === 'subscriber' && (
                                            <div className="text-[10px] text-amber-700 mt-1 whitespace-nowrap">
                                                Val.: {u.subscriptionExpiresAt.toLocaleDateString()}
                                                {u.subscriptionExpiresAt < new Date() && <span className="text-red-500 ml-1 font-bold">(Exp.)</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-right w-[20%]">
                                        {u.role !== 'admin' && (
                                            <div className="flex flex-col items-end gap-1">
                                                <div className="flex items-center gap-1">
                                                    {u.role === 'subscriber' && (
                                                        <button onClick={() => changeRole(u.id, 'user')} className="text-[10px] bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-100 uppercase font-bold" title="Remover VIP">Revogar VIP</button>
                                                    )}
                                                    <button
                                                        onClick={() => openBonusModal(u.id, u.name)}
                                                        className="flex items-center gap-1 text-[10px] bg-amber-500 hover:bg-amber-600 text-white font-bold py-1 px-3 rounded uppercase transition-colors"
                                                    >
                                                        <Gift size={12} /> Bônus
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-right w-[20%]">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleResetAccount(u.id)}
                                                className="p-2 rounded border border-transparent text-amber-500 hover:bg-amber-50 hover:border-amber-200 transition-colors"
                                                title="Zerar Progresso do Jogador"
                                            >
                                                <RefreshCw size={16} />
                                            </button>
                                            <button
                                                onClick={() => changeRole(u.id, u.role === 'admin' ? 'user' : 'admin')}
                                                className={`p-2 rounded hover:bg-gray-100 border ${u.role === 'admin' ? 'text-purple-600 border-purple-200 bg-purple-50' : 'text-gray-400 border-transparent'}`}
                                                title={u.role === 'admin' ? "Rebaixar para User" : "Promover a Admin"}
                                            >
                                                <Shield size={16} />
                                            </button>
                                            <button
                                                onClick={() => toggleBan(u.id, u.status)}
                                                className={`p-2 rounded border border-transparent ${u.status === 'active' ? 'text-red-400 hover:bg-red-50 hover:border-red-200' : 'text-green-600 hover:bg-green-50 border-green-200 bg-green-50'}`}
                                                title={u.status === 'active' ? "Banir" : "Reativar"}
                                            >
                                                {u.status === 'active' ? <Ban size={16} /> : <CheckCircle size={16} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="mt-auto p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 shrink-0">
                    <div className="text-sm text-gray-500 hidden sm:block">
                        Mostrando <span className="font-bold text-gray-900">{filteredUsers.length === 0 ? 0 : (currentPage - 1) * usersPerPage + 1}</span> a <span className="font-bold text-gray-900">{Math.min(currentPage * usersPerPage, filteredUsers.length)}</span> de <span className="font-bold text-gray-900">{filteredUsers.length}</span>
                    </div>
                    <div className="flex gap-2 mx-auto sm:mx-0">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className="p-2 border border-gray-300 rounded bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <div className="px-4 py-2 text-sm font-bold text-gray-700">
                            Página {currentPage} de {totalPages}
                        </div>
                        <button
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-2 border border-gray-300 rounded bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Bonus Modal */}
            {bonusModal.isOpen && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-2">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Gift className="text-amber-500" /> Bonificar Jogador
                            </h3>
                            <button onClick={closeBonusModal} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">
                            Você está aplicando um bônus manual para: <strong className="text-gray-900">{bonusModal.userName}</strong>. Todas as concessões ficam registradas no livro auditor.
                        </p>

                        <form onSubmit={handleApplyBonus} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo de Recompensa</label>
                                <select
                                    value={bonusModal.rewardType}
                                    onChange={(e) => setBonusModal({ ...bonusModal, rewardType: e.target.value as any })}
                                    className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-amber-500"
                                >
                                    <option value="lupas">Lupas (Energia)</option>
                                    <option value="keys">Chaves de Arquivo</option>
                                    <option value="vip_days">Dias VIP</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quantidade</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={bonusModal.amount}
                                    onChange={(e) => setBonusModal({ ...bonusModal, amount: parseInt(e.target.value) || 0 })}
                                    className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-amber-500 font-bold text-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Motivo / Protocolo (Para o Log)</label>
                                <input
                                    type="text"
                                    value={bonusModal.reason}
                                    onChange={(e) => setBonusModal({ ...bonusModal, reason: e.target.value })}
                                    placeholder="Ex: Erro de pagamento #123, Cortesia de Live..."
                                    className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-amber-500 text-sm"
                                    required
                                />
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100 mt-6">
                                <button
                                    type="button"
                                    onClick={closeBonusModal}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 font-bold transition-colors"
                                >
                                    CANCELAR
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 font-bold transition-colors"
                                >
                                    CONFIRMAR E ENVIAR
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManager;
